// musicPlayer.js
export class MusicPlayer {
  constructor() {
    this.audioContext = null;
    this.currentSources = [];
    this.progressInterval = null;
    this.cleanupTimeout = null;
    this.isPlaying = false;
    this.playbackSpeed = 1.0;
    this.isUserInteraction = false;
    this.masterGain = null;
    this.currentPhrase = null;
    this.phraseStartTime = 0;
    this.noteEvents = [];
  }

  noteToFrequency(note) {
    const noteMap = {
      E2: 82.41, F2: 87.31, 'F#2': 92.5, G2: 98.0, 'G#2': 103.83,
      A2: 110.0, 'A#2': 116.54, B2: 123.47,
      C3: 130.81, 'C#3': 138.59, D3: 146.83, 'D#3': 155.56, E3: 164.81,
      F3: 174.61, 'F#3': 185.0, G3: 196.0, 'G#3': 207.65, A3: 220.0,
      'A#3': 233.08, B3: 246.94, C4: 261.63, D4: 293.66, E4: 329.63
    };
    return noteMap[note] || 130.81;
  }

  async ensureAudioContext() {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();
      this.audioContext.latencyHint = 'interactive';

      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = 1.5;
      this.masterGain.connect(this.audioContext.destination);
    }

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    return this.audioContext;
  }

  async stopPlayback() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
      this.cleanupTimeout = null;
    }

    this.currentSources.forEach((source) => {
      try {
        if (source && typeof source.stop === 'function') {
          source.stop();
        }
      } catch (_) {}
    });

    this.currentSources = [];
    this.noteEvents = [];
    this.isPlaying = false;
    this.currentPhrase = null;

    return 0;
  }

  setPlaybackSpeed(speed) {
    this.playbackSpeed = Math.max(0.25, Math.min(2.0, speed));
  }

  setVolume(level) {
    if (this.masterGain) {
      const volume = Math.max(0, Math.min(3.0, level));
      this.masterGain.gain.value = volume;
    }
  }

  createSpeechNote(fundamentalFreq, duration, context, startTime, isMaleVoice = true) {
    const sources = [];

    const oscillator = context.createOscillator();
    const gainNode = context.createGain();
    oscillator.connect(gainNode);

    oscillator.type = 'triangle';
    oscillator.frequency.value = fundamentalFreq;

    if (isMaleVoice) {
      const filter = context.createBiquadFilter();
      filter.type = 'lowshelf';
      filter.frequency.value = 400;
      filter.gain.value = 8.0;
      gainNode.connect(filter);
      filter.connect(this.masterGain);
    } else {
      const filter = context.createBiquadFilter();
      filter.type = 'peaking';
      filter.frequency.value = 1200;
      filter.Q.value = 1.0;
      filter.gain.value = 6.0;
      gainNode.connect(filter);
      filter.connect(this.masterGain);
    }

    const attack = Math.min(0.06, duration * 0.15);
    const decay = Math.min(0.08, duration * 0.2);
    const sustain = 0.7;
    const release = Math.min(0.12, duration * 0.3);

    const endTime = startTime + duration;
    const peakGain = isMaleVoice ? 0.8 : 0.7;

    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(peakGain, startTime + attack);
    gainNode.gain.linearRampToValueAtTime(peakGain * sustain, startTime + attack + decay);
    gainNode.gain.linearRampToValueAtTime(peakGain * sustain, Math.max(startTime + attack + decay, endTime - release));
    gainNode.gain.linearRampToValueAtTime(0, endTime);

    oscillator.start(startTime);
    oscillator.stop(endTime + 0.1);
    sources.push(oscillator);

    if (isMaleVoice && fundamentalFreq < 150) {
      const harmonic = context.createOscillator();
      const harmonicGain = context.createGain();
      harmonic.connect(harmonicGain);
      harmonicGain.connect(this.masterGain);

      harmonic.type = 'sine';
      harmonic.frequency.value = fundamentalFreq * 2;

      harmonicGain.gain.setValueAtTime(0, startTime);
      harmonicGain.gain.linearRampToValueAtTime(0.15, startTime + attack);
      harmonicGain.gain.linearRampToValueAtTime(0.1, startTime + attack + decay);
      harmonicGain.gain.linearRampToValueAtTime(0.1, Math.max(startTime + attack + decay, endTime - release));
      harmonicGain.gain.linearRampToValueAtTime(0, endTime);

      harmonic.start(startTime);
      harmonic.stop(endTime + 0.1);
      sources.push(harmonic);
    }

    return sources;
  }

  getActiveNoteIndex(elapsedSeconds) {
    for (const event of this.noteEvents) {
      if (
        !event.isPause &&
        elapsedSeconds >= event.startTime &&
        elapsedSeconds < event.endTime
      ) {
        return event.noteIndex;
      }
    }
    return null;
  }

  async playNotes(notes, progressCallback) {
    await this.stopPlayback();
    await new Promise((resolve) => setTimeout(resolve, 60));

    try {
      const audioContext = await this.ensureAudioContext();
      this.isPlaying = true;

      if (progressCallback) {
        progressCallback({ progress: 0, activeNoteIndex: null });
      }

      const sources = [];
      const noteEvents = [];
      let totalPlaybackTime = 0;

      for (let i = 0; i < notes.length; i++) {
        const note = notes[i];

        if (note.isPause || note.isUnvoiced || note.type === 'rest' || note.musicalNote === 'REST') {
          const pauseDuration = (note.duration || 0.3) / this.playbackSpeed;

          noteEvents.push({
            isPause: true,
            noteIndex: null,
            duration: pauseDuration,
            startTime: totalPlaybackTime,
            endTime: totalPlaybackTime + pauseDuration
          });

          totalPlaybackTime += pauseDuration;
          continue;
        }

        if (note.musicalNote && note.duration) {
          const frequency = this.noteToFrequency(note.musicalNote);

          // IMPORTANT: no 0.8 shrink factor anymore
          const baseDuration = Math.max(0.08, note.duration);
          const playbackDuration = baseDuration / this.playbackSpeed;

          const gap = 0.015;
          const startTime = totalPlaybackTime + (i > 0 ? gap : 0);
          const endTime = startTime + playbackDuration;

          noteEvents.push({
            isPause: false,
            noteIndex: i,
            frequency,
            duration: playbackDuration,
            startTime,
            endTime,
            isMaleVoice: frequency < 200
          });

          totalPlaybackTime = endTime;
        }
      }

      this.noteEvents = noteEvents;
      this.currentPhrase = notes;
      this.phraseStartTime = Date.now();

      const baseTime = audioContext.currentTime + 0.08;

      noteEvents.forEach((event) => {
        if (event.isPause) return;

        const voiceSources = this.createSpeechNote(
          event.frequency,
          event.duration,
          audioContext,
          baseTime + event.startTime,
          event.isMaleVoice
        );

        sources.push(...voiceSources);
      });

      this.currentSources = sources;

      this.progressInterval = setInterval(() => {
        const elapsed = (Date.now() - this.phraseStartTime) / 1000;
        const progress =
          totalPlaybackTime > 0
            ? Math.min(100, (elapsed / totalPlaybackTime) * 100)
            : 100;

        const activeNoteIndex = this.getActiveNoteIndex(elapsed);

        if (progressCallback) {
          progressCallback({ progress, activeNoteIndex });
        }

        if (progress >= 100) {
          this.completePlayback(progressCallback);
        }
      }, this.isMobileDevice() ? 120 : 60);

      this.cleanupTimeout = setTimeout(() => {
        this.completePlayback(progressCallback);
      }, totalPlaybackTime * 1000 + 400);
    } catch (error) {
      await this.stopPlayback();
      throw new Error(
        error.message.includes('user interaction')
          ? 'Please tap play again to start audio'
          : 'Playback failed'
      );
    }
  }

  completePlayback(progressCallback) {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    this.isPlaying = false;

    if (progressCallback) {
      progressCallback({ progress: 100, activeNoteIndex: null });
    }
  }

  getPlayerState() {
    return {
      isPlaying: this.isPlaying,
      playbackSpeed: this.playbackSpeed,
      currentPhrase: this.currentPhrase,
      canPlay: this.audioContext && this.audioContext.state !== 'closed'
    };
  }

  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
  }

  async quickPlay(notes, onProgress, onError) {
    try {
      await this.playNotes(notes, onProgress);
    } catch (error) {
      if (onError) onError(error.message);
    }
  }

  destroy() {
    this.stopPlayback().catch(() => {});
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {});
    }
  }
}

export const musicPlayer = new MusicPlayer();